import React from 'react';
import {createRoot} from 'react-dom/client';
import {HashRouter} from "react-router-dom";
import itLocale from 'date-fns/locale/it';

import * as Sentry from "@sentry/react";
import {Integrations} from "@sentry/tracing";

import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";

import {CssBaseline} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {LocalizationProvider} from '@mui/x-date-pickers';
import {itIT} from '@mui/x-date-pickers/locales';

import App from './App';
import theme from "./theme";
import {PWAProvider} from "./Pwa";
import {AuthenticationProvider} from "./Auth";
import {ApiError} from "./models/ApiError";
import {FeedbackProvider} from "./components/FeedbackTools";
import GlobalStateProvider from "./GlobalState";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

/*
 * Configure sentry (we disable sentry on DEV)
 */
if (process.env.NODE_ENV === "production") {
    Sentry.init({
        dsn: "https://f7f3ffa1797e4da4961480a25637b213@o818844.ingest.sentry.io/5877031",
        release: process.env.REACT_APP_VERSION,
        environment: process.env.NODE_ENV,
        integrations: [new Integrations.BrowserTracing()],
        tracesSampleRate: 0.1,
    });
}

/*
 * Workaround to set the height of 100vh
 * It is required because mobile browser can hide the navigation bar
 * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
 */
const setCssHeightVariable = () => {
    // Get the viewport height and we multiple it by 1% to get a value for a vh unit.
    // innerHeight is always rounded to the closest integer, so we need to subtract -0.5 to avoid the scroll bar with a 100vh
    let vh = (window.innerHeight - 0.5) * 0.01;
    // Set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}
window.addEventListener('resize', setCssHeightVariable);
setCssHeightVariable();

/*
 * Print current version on console
 */
console.log(`%cLivignoItaly_FE v${process.env.REACT_APP_VERSION}`, "color:green; font-size: 36px; font-weight: bold");

/*
 * Configure error parser
 */
const errorParser = (e) => {
    let notification = {
        title: "ERRORE SCONOSCIUTO",
        messages: [],
    };

    // If notification is not define return
    if (!e) {
        notification.title = "ERRORE";
        notification.messages = ["Errore non definito"];
        return notification;
    }

    if (e instanceof ApiError) {
        notification.title = e.title || "Errore server";
        notification.messages = e.descriptionsArray || ["Si è verificato un errore sul server"];
    } else if (e instanceof Error) {
        notification.title = "ERRORE";
        notification.messages = [e.message] || ["Errore dell'applicazione"];
    } else if (typeof e === "string") {
        notification.title = "ERRORE";
        notification.messages = [e]
    }

    return notification;
};

/*
 * Define a global Query client that keep the server state
 */
const queryClient = new QueryClient();


/*
 * Define the locale used by the date picker
 */
const localeText = {
    ...itIT.components.MuiLocalizationProvider.defaultProps.localeText,
    cancelButtonLabel: "Cancella"
};


/*
 * Render the application
 */
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <ThemeProvider theme={theme}>
        <CssBaseline/>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale} localeText={localeText}>
            <HashRouter>
                <FeedbackProvider errorParser={errorParser}>
                    <PWAProvider>
                        <QueryClientProvider client={queryClient}>
                            <GlobalStateProvider>
                                <AuthenticationProvider>
                                    <App/>
                                    <ReactQueryDevtools initialIsOpen={false}/>
                                </AuthenticationProvider>
                            </GlobalStateProvider>
                        </QueryClientProvider>
                    </PWAProvider>
                </FeedbackProvider>
            </HashRouter>
        </LocalizationProvider>
    </ThemeProvider>,
);

// Register the service worker.
// This cannot be done in a useEffect because the service worker must be registered before the first render
// of the application otherwise the load event that actually register the service worker might be missed.
serviceWorkerRegistration.register({
    onUpdate: () => {
        // fire an event to the PWA context to notify the user that a new version is available
        window.dispatchEvent(new Event('onServiceWorkerUpdate'));
    }
});

